'use client';

import { useCallback, useMemo, useState } from 'react';
import type { VehicleCardModel } from '@/features/vehicle';
import { activeFilters, removeActiveFilter, type VehicleActiveFilter } from './vehicleActiveFilters';
import { facetOptions } from './vehicleFacetOptions';
import { EMPTY_VEHICLE_FILTERS, filterBounds, matchesFilters, type VehicleFilters } from './vehicleFilters';
import type { VehicleSortKey, VehicleView } from './vehicleListing.constants';
import { pageState } from './vehiclePagination';
import { sortVehicles } from './vehicleSort';
import { useVehiclePageParams } from './useVehiclePageParams';

/** Filter, sort, view and page of the listing, and everything derived from them. */
export function useVehicleListing(vehicles: VehicleCardModel[]) {
    const [filters, setFilters] = useState<VehicleFilters>(EMPTY_VEHICLE_FILTERS);
    const [sort, setSort] = useState<VehicleSortKey>('newest');
    const [view, setView] = useState<VehicleView>('grid');
    const { page, perPage, setPage, setPerPage } = useVehiclePageParams();

    const bounds = useMemo(() => filterBounds(vehicles), [vehicles]);
    const options = useMemo(() => facetOptions(vehicles, filters), [vehicles, filters]);
    const chips = useMemo(() => activeFilters(filters, bounds), [filters, bounds]);
    const results = useMemo(
        () =>
            sortVehicles(
                vehicles.filter((vehicle) => matchesFilters(vehicle, filters)),
                sort,
            ),
        [vehicles, filters, sort],
    );

    const pagination = pageState(results.length, perPage, page);
    const pageVehicles = results.slice(pagination.from ? pagination.from - 1 : 0, pagination.to);

    // Every change of the selection starts over on page one.
    const changeFilters = useCallback(
        (next: VehicleFilters) => {
            setFilters(next);
            setPage(1);
        },
        [setPage],
    );

    const changeSort = useCallback(
        (next: VehicleSortKey) => {
            setSort(next);
            setPage(1);
        },
        [setPage],
    );

    const removeChip = useCallback(
        (chip: VehicleActiveFilter) => changeFilters(removeActiveFilter(filters, chip)),
        [changeFilters, filters],
    );

    const resetFilters = useCallback(() => changeFilters(EMPTY_VEHICLE_FILTERS), [changeFilters]);

    return {
        filters,
        changeFilters,
        resetFilters,
        removeChip,
        chips,
        options,
        bounds,
        sort,
        changeSort,
        view,
        setView,
        total: results.length,
        pageVehicles,
        pagination,
        perPage,
        setPage,
        setPerPage,
    };
}

export type VehicleListingState = ReturnType<typeof useVehicleListing>;
